import { KeyboardArrowDown } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState, type FC } from "react";

export interface FilterOption {
  readonly label: string;
  readonly value: string;
}

interface SearchableMultiSelectFilterProps {
  readonly label: string;
  readonly options: FilterOption[];
  readonly selectedValues: string[];
  readonly onChange: (values: string[]) => void;
}

const SearchableMultiSelectFilter: FC<SearchableMultiSelectFilterProps> = ({
  label,
  options,
  selectedValues,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  const open = Boolean(anchorEl);
  const filteredOptions = options.filter((option) =>
    option.label.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
  );

  return (
    <Box>
      <Badge
        badgeContent={selectedValues.length || null}
        color="primary"
        overlap="rectangular"
        sx={{ "& .MuiBadge-badge": { right: 6, top: 6 } }}
      >
        <Button
          size="small"
          variant={selectedValues.length > 0 ? "contained" : "outlined"}
          color={selectedValues.length > 0 ? "primary" : "inherit"}
          endIcon={<KeyboardArrowDown />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderColor: selectedValues.length > 0 ? "primary.main" : "rgba(0,0,0,0.18)",
            color: selectedValues.length > 0 ? undefined : "text.primary",
            bgcolor: selectedValues.length > 0 ? undefined : "#fff",
            px: 1.5,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Button>
      </Badge>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: 320,
              maxWidth: "92vw",
              borderRadius: 1,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              border: "1px solid rgba(0,0,0,0.08)",
            },
          },
        }}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <Stack direction="row" sx={{ mb: 1, justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {label}
            </Typography>
            {selectedValues.length > 0 && (
              <Button
                size="small"
                color="inherit"
                onClick={() => onChange([])}
                sx={{ textTransform: "none", minWidth: 0 }}
              >
                Wyczyść
              </Button>
            )}
          </Stack>

          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Szukaj..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ mb: 1, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Box
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              px: 1.5,
              pb: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 0.15,
            }}
          >
            {filteredOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                control={<Checkbox size="small" />}
                checked={selectedValues.includes(option.value)}
                onChange={(_, checked) => {
                  onChange(
                    checked
                      ? [...selectedValues, option.value]
                      : selectedValues.filter((value) => value !== option.value),
                  );
                }}
                label={<Typography variant="body2">{option.label}</Typography>}
              />
            ))}
            {filteredOptions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                Brak wyników
              </Typography>
            )}
          </Box>
          <Divider />
          <Box sx={{ p: 1.25, display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => setAnchorEl(null)}
              sx={{ textTransform: "none", borderRadius: 1 }}
            >
              Gotowe
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default SearchableMultiSelectFilter;
